import type {HydratedAPITask} from '@github-ui/agent-sessions/types/api-task'
import {
  getReadableSessionState,
  isSessionState,
  SessionState,
  TERMINAL_SESSION_STATES,
} from '@github-ui/agent-sessions/types/session'
import {calculateElapsedTime, hasCompletedAt} from '@github-ui/agent-sessions/utils/calculateElapsedTime'
import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {getLeadingVisualIconAttributes} from '@github-ui/agent-sessions/utils/get-leading-visual-icon-attributes'
import {canViewerOpenTaskPanel} from '@github-ui/issue-agent-tasks/utils/cli-task-visibility'
import {useUser} from '@github-ui/use-user'
import {ClockIcon, CopilotIcon, type Icon, SkipIcon} from '@primer/octicons-react'
import {Token, Tooltip, VisuallyHidden} from '@primer/react'
import {type MouseEvent, useState} from 'react'

import type {BoardPayloadAgentTask} from '../board-payload'
import {useAgentSessionStatus} from './agent-session-statuses'
import styles from './AgentSessionPill.module.css'

export type AgentSessionOpenTarget = {taskId: string; repoId: number}

const PRIVATE_SESSION = 'Session log is private to its creator'

// Circular glyphs, drawn smaller to match the check's weight.
const ROUND_ICONS: Partial<Record<SessionState, Icon>> = {
  [SessionState.Queued]: ClockIcon,
  [SessionState.Failed]: SkipIcon,
  [SessionState.Cancelled]: SkipIcon,
}
const MUTED_STATES: ReadonlySet<SessionState> = new Set([SessionState.Queued, SessionState.Failed])

/** A card's newest agent session. A plain click opens it in the side panel; a modified click follows the link. */
export function AgentSessionPill({
  agentTask,
  onOpen,
}: {
  agentTask: BoardPayloadAgentTask
  onOpen: (target: AgentSessionOpenTarget, returnFocusElement: HTMLElement) => void
}) {
  const status = useAgentSessionStatus(agentTask)
  // The viewer is typed as a string, but the server sends the numeric id the visibility check compares.
  const {currentUser} = useUser()
  const viewerId = currentUser ? Number(currentUser.id) : undefined
  // Refreshed on hover and focus instead of a timer per card.
  const [now, setNow] = useState(() => Date.now())
  const refreshNow = () => setNow(Date.now())

  if (status.kind === 'missing') return null

  const agentName = agentTask.agent.login
  if (status.kind === 'queued' || !agentTask.id) {
    return <Pill agentName={agentName} state={SessionState.Queued} />
  }

  const taskId = agentTask.id
  const state = status.kind === 'loaded' ? status.state : undefined

  if (status.kind === 'loaded' && !canViewerOpenTaskPanel(status.task, viewerId)) {
    return (
      <Tooltip text={PRIVATE_SESSION} type="description" direction="n">
        <Pill as="button" agentName={agentName} state={state} aria-disabled />
      </Tooltip>
    )
  }

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!isPlainLeftClick(event)) return
    event.preventDefault()
    onOpen({taskId, repoId: agentTask.repoId}, event.currentTarget)
  }

  const href = generateTaskPathForRepo({taskId, repositoryNwo: agentTask.repoNwo}) || undefined
  const description =
    status.kind === 'loaded' ? describe(agentName, status.task, status.state, now) : `Open ${agentName} session`

  // Always wrapped, so the pill keeps its focus when its status arrives.
  return (
    <Tooltip text={description} type="description" direction="n">
      <Pill
        as={href ? 'a' : 'button'}
        href={href}
        agentName={agentName}
        state={state}
        onClick={handleClick}
        onMouseEnter={refreshNow}
        onFocus={refreshNow}
      />
    </Tooltip>
  )
}

type PillProps = {
  agentName: string
  state: string | undefined
  as?: 'a' | 'button'
  href?: string
  onClick?: (event: MouseEvent<HTMLElement>) => void
  onMouseEnter?: () => void
  onFocus?: () => void
  'aria-disabled'?: boolean
}

function Pill({agentName, state, ...props}: PillProps) {
  const sessionState = state ? toSessionState(state) : undefined
  const {icon: StatusIcon, color} = state
    ? getLeadingVisualIconAttributes(undefined, false, sessionState, false)
    : {icon: CopilotIcon, color: 'var(--fgColor-muted)'}
  const label = state ? pillLabel(state) : `${agentName} session`
  const RoundIcon = sessionState ? ROUND_ICONS[sessionState] : undefined
  const iconColor = sessionState && MUTED_STATES.has(sessionState) ? 'var(--fgColor-muted)' : color

  return (
    <Token
      {...props}
      className={styles.pill}
      leadingVisual={() => (
        <span className={styles.icon} style={{color: iconColor}} aria-hidden="true">
          {RoundIcon ? <RoundIcon size={12} /> : <StatusIcon />}
        </span>
      )}
      text={
        <>
          {state ? <VisuallyHidden>{`${agentName} session: `}</VisuallyHidden> : null}
          {label}
        </>
      }
    />
  )
}

/** Groups states as the issue header does. */
function toSessionState(state: string): SessionState | undefined {
  if (state === 'running' || state === SessionState.Idle) return SessionState.InProgress
  if (state === SessionState.TimedOut) return SessionState.Failed
  return isSessionState(state) ? state : undefined
}

function pillLabel(state: string): string {
  const sessionState = toSessionState(state)
  switch (sessionState) {
    case SessionState.Queued:
      return 'Initializing session'
    case SessionState.InProgress:
      return 'In progress'
    case SessionState.Completed:
      return 'Complete'
    default:
      return getReadableSessionState(sessionState)
  }
}

function describe(agentName: string, task: HydratedAPITask, state: string, now: number): string {
  const sessionState = toSessionState(state)
  const updatedAt = task.updated_at ?? task.last_updated_at ?? task.created_at
  const session = task.sessions.reduce<HydratedAPITask['sessions'][number] | undefined>(
    (latest, candidate) =>
      !latest || Date.parse(candidate.created_at) > Date.parse(latest.created_at) ? candidate : latest,
    undefined,
  )
  const startedAt = session?.created_at ?? task.created_at
  const completedAt = session?.completed_at
  const isRunning = sessionState === SessionState.InProgress || sessionState === SessionState.Queued
  const isFinished = !!sessionState && TERMINAL_SESSION_STATES.has(sessionState)
  const endedAt = isRunning ? new Date(now) : hasCompletedAt(completedAt) ? completedAt : updatedAt

  const elapsed = calculateElapsedTime(startedAt, endedAt)
  return [
    task.name ? `${agentName}: ${task.name}` : agentName,
    `Updated ${calculateElapsedTime(updatedAt, new Date(now))} ago`,
    isRunning ? `Running for ${elapsed}` : isFinished ? `Ran for ${elapsed}` : `Active for ${elapsed}`,
  ].join(' · ')
}

function isPlainLeftClick(event: MouseEvent<HTMLElement>): boolean {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- a modified link click, not a keyboard shortcut
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}
